import { Box, Button, Dialog, DialogContent, IconButton } from '@mui/material'
import { Close } from '@mui/icons-material'
import { NextRouter, useRouter } from 'next/router'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { claimFreeMonthSubscription } from '@dtx-company/inter-app/src/api/subscription-service/claimFreeMonthSubscription'
import { fireHeapEvent } from '@dtx-company/inter-app/src/event-tracking/helpers'
import { getPlanTypeName } from '../utils/getPlanTypeName'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCurrentSubscriptionSWR } from '@dtx-company/inter-app/src/hooks/useCurrentSubscriptionSWR'
import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import styled from '@emotion/styled'
import theme from '../theme'

function getSourceForHeap(router: NextRouter): string {
  const param = Array.isArray(router.query.from) ? router.query.from[0] : router.query.from
  if (param) {
    return param
  }
  return router.pathname
}

const _CloseIconButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  color: ${theme.colors.primary.black};
`

const _HeadingText = styled(Text)`
  padding-top: 36px;
  text-align: center;
`

const _ImageBox = styled(Box)`
  margin-top: 32px;
  text-align: center;
`

const _BodyText = styled(Text)`
  margin-top: 32px;
  text-align: center;
`

const _ActionsBox = styled(Box)`
  margin-top: 40px;
`

const _ClaimMonthButton = styled(Button)`
  display: flex;
  background-color: ${theme.colors.primary.flowBlue};
  color: ${theme.colors.primary.white};
  padding: 10px 16px;
  text-transform: none;
  width: 100%;

  &:hover {
    background-color: ${theme.colors.primary.flowBlue};
  }
`

const _NoThanksButton = styled(Button)`
  display: flex;
  border-color: ${theme.colors.primary.flowBlue};
  color: ${theme.colors.primary.flowBlue};
  margin-top: 16px;
  padding: 10px 16px;
  text-transform: none;
  width: 100%;

  &:hover {
    border-color: ${theme.colors.primary.flowBlue};
  }
`

interface ClaimFreeMonthDialogProps {
  open: boolean
  onAcceptSuccess: () => void
  onDecline: () => void
  onClose: () => void
}

export function ClaimFreeMonthDialog(props: ClaimFreeMonthDialogProps): JSX.Element {
  const [isClaiming, setIsClaiming] = useState(false)

  const currentSubscriptionInfoSWR = useCurrentSubscriptionSWR(
    {},
    {
      revalidateIfStale: false,
      revalidateOnFocus: false
    }
  )
  const currentPlan = currentSubscriptionInfoSWR.data?.plan
  const currentSubscriptionId = currentSubscriptionInfoSWR.data?.id

  const authState = useAuthState()
  const router = useRouter()

  function handleOnCloseClick(): void {
    if (isClaiming) return
    props.onClose()
  }

  function handleOnDeclineClick(): void {
    if (isClaiming) return
    props.onDecline()
  }

  async function handleOnAcceptClick(): Promise<void> {
    if (!authState.token) return
    if (!currentSubscriptionId) return
    if (isClaiming) return
    setIsClaiming(true)
    try {
      await claimFreeMonthSubscription({
        token: authState.token,
        subscriptionId: currentSubscriptionId
      })
      fireHeapEvent('Revenue_ClaimFreeMonth_Claimed_FreeMonth', {
        source: getSourceForHeap(router),
        currentPlanType: currentPlan?.type,
        success: true
      })
      props.onAcceptSuccess()
    } catch {
      fireHeapEvent('Revenue_ClaimFreeMonth_Claimed_FreeMonth', {
        source: getSourceForHeap(router),
        currentPlanType: currentPlan?.type,
        success: false
      })
      sendErrorNotification(`Sorry something went wrong, please try again or refresh the page.`)
    } finally {
      setIsClaiming(false)
    }
  }

  const headingText = useMemo(() => {
    if (!currentPlan?.type) return
    return `Here's a free month of ${getPlanTypeName(currentPlan.type)} on us!`
  }, [currentPlan?.type])

  useEffect(() => {
    if (!props.open) return
    fireHeapEvent('Revenue_ClaimFreeMonth_Opened_Dialog', {
      source: getSourceForHeap(router)
    })
  }, [props.open, router])

  return (
    <>
      <Dialog
        fullWidth={true}
        PaperProps={{
          sx: {
            maxWidth: 536
          }
        }}
        open={props.open}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') {
            fireHeapEvent('Revenue_ClaimFreeMonth_Closed_Dialog', {
              source: getSourceForHeap(router),
              closeType: 'escape'
            })
            handleOnCloseClick()
          }
        }}
      >
        <_CloseIconButton
          aria-label="close"
          onClick={() => {
            fireHeapEvent('Revenue_ClaimFreeMonth_Closed_Dialog', {
              source: getSourceForHeap(router),
              closeType: 'x_button'
            })
            handleOnCloseClick()
          }}
          data-testid="ClaimFreeMonthDialog_CloseX"
        >
          <Close />
        </_CloseIconButton>
        <DialogContent>
          <_HeadingText component="h2" variant="display/small">
            {headingText}
          </_HeadingText>
          <_ImageBox>
            <Image
              src="/images/box-of-flowcodes-and-confetti.png"
              height={233}
              width={270}
              alt=""
            />
          </_ImageBox>
          <_BodyText component="p" variant="body/medium">
            We really appreciate you being one of our customers. As a thank you, next month is on
            us.
          </_BodyText>
          <_ActionsBox>
            <_ClaimMonthButton
              variant="contained"
              color="primary"
              disabled={isClaiming}
              disableRipple
              onClick={() => {
                fireHeapEvent('Revenue_ClaimFreeMonth_Clicked_ClaimButton', {
                  source: getSourceForHeap(router)
                })
                handleOnAcceptClick()
              }}
            >
              <Text variant="button/general" color={theme.colors.primary.white}>
                Claim my free month
              </Text>
            </_ClaimMonthButton>
            <_NoThanksButton
              variant="outlined"
              color="primary"
              disableRipple
              onClick={() => {
                fireHeapEvent('Revenue_ClaimFreeMonth_Closed_Dialog', {
                  source: getSourceForHeap(router),
                  closeType: 'decline_button'
                })
                handleOnDeclineClick()
              }}
            >
              <Text variant="button/general" color={theme.colors.primary.flowBlue}>
                No thanks
              </Text>
            </_NoThanksButton>
          </_ActionsBox>
        </DialogContent>
      </Dialog>
    </>
  )
}
